import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { debounce, Box, Stack, Typography } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@center/ui-kit'

import { useRegisterBreadcrumbAction } from 'components/AuthenticatedLayout/Header/components/BreadcrumbActions'
import HeaderExportButton from 'components/global/Buttons/HeaderExportButton'
import TextField from 'components/global/CUK/TextField'
import { ContentFilters } from '../styles'
import { DatePicker, DatePickerAnchorField } from 'components/global/CUK/DatePicker'
import Switch from 'components/Switch'
import { useUser } from 'store'
import StatusSelect from './StatusSelect'
import RolesSelect from './RolesSelect'
import SynchronizeSelect from './SynchronizeSelect'
import useFiltersStore from '../../store'
import { getDateRangeKind, trackEvent } from 'analytics'

const Filters = () => {
  const { t } = useTranslation()
  const [user] = useUser()
  const [filters, setFilters] = useFiltersStore()
  const [verbatim, setVerbatim] = useState(filters.verbatim)
  const searchStartedRef = useRef(false)
  const location = useLocation()
  const navigate = useNavigate()

  const methods = useForm()
  const handleChange = newFilters => {
    setFilters(newFilters)
  }

  const debouncedChange = useMemo(
    () =>
      debounce(value => {
        handleChange({ ...filters, verbatim: value })
      }, 500),
    [filters]
  )

  const handleSearchChange = newValue => {
    const value = newValue.toString()
    setVerbatim(value)
    debouncedChange(value)

    if (value.length > 2 && !searchStartedRef.current) {
      trackEvent('search_user')
      searchStartedRef.current = true
    } else if (value.length <= 2) {
      searchStartedRef.current = false
    }
  }
  const exportAction = useMemo(() => (
    <HeaderExportButton
      analyticsName="users"
      onClick={() => navigate('export')}
      disabled={Boolean(user.demo_mode)}
    >
      {t('export.button')}
    </HeaderExportButton>
  ), [navigate, t, user.demo_mode])

  useRegisterBreadcrumbAction(exportAction)

  return (
    <ContentFilters>
      <FormProvider {...methods}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%'
          }}>
          <Stack direction="row" spacing={2}>
            <TextField
              value={verbatim}
              setvalue={handleSearchChange}
              width={'100%'}
              type="text"
              input={{
                placeholder: t('auth.admin.search_placeholder'),
                starticon: <i className={'fa-light fa-search'} />
              }}
            />
            {
              !filters.internal && (
                <SynchronizeSelect
                  onChange={values => {
                    handleChange({ ...filters, sync: values })
                    trackEvent('users_filter_changed', {
                      filter_name: 'synchronization',
                      filter_value: values.length ? values.join(',') : 'all',
                      selected_count: values.length
                    })
                  }}
                  values={filters.sync}
                />
              )
            }
            {filters.internal && (
              <>
                <StatusSelect
                  onChange={values => {
                    handleChange({ ...filters, status: values })
                    trackEvent('users_filter_changed', {
                      filter_name: 'status',
                      filter_value: values.length ? values.join(',') : 'all',
                      selected_count: values.length
                    })
                  }}
                  values={filters.status}
                />
                <RolesSelect
                  onChange={values => {
                    handleChange({ ...filters, roles: values })
                    trackEvent('users_filter_changed', {
                      filter_name: 'role',
                      filter_value: values.length ? values.join(',') : 'all',
                      selected_count: values.length
                    })
                  }}
                  values={filters.roles}
                />
              </>
            )}
            <DatePicker
              placement='left-start'
              showPresets={true}
              blockFutureDates={true}
              defaultStartDate={filters.created_at_start}
              defaultEndDate={filters.created_at_end}
              field={
                <DatePickerAnchorField
                  isdate
                  inputSize="small"
                  locale="fr"
                  startDate={filters?.created_at_start}
                  endDate={filters.created_at_end}
                  onReset={() => {
                    handleChange({
                      ...filters,
                      created_at_start: null,
                      created_at_end: null
                    })
                    trackEvent('users_filter_changed', {
                      filter_name: 'creation_date',
                      filter_value: 'empty'
                    })
                  }}
                  placeholder={t('auth.admin.date_placeholder')}
                />
              }
              onSubmit={(created_at_start, created_at_end) => {
                handleChange({
                  ...filters,
                  created_at_start,
                  created_at_end
                })
                trackEvent('users_filter_changed', {
                  filter_name: 'creation_date',
                  filter_value: getDateRangeKind(created_at_start, created_at_end)
                })
              }}
            />
          </Stack>
          <Box display={'flex'} gap={2}>
            {(user?.admin || user?.internal) && (
              <Switch
                label={<Typography sx={{ fontWeight: 400, fontSize: '0.75rem' }}>Comptes Wifirst internes</Typography>}
                checked={filters.internal}
                componentsProps={{ typography: { variant: 'caption' } }}
                onChange={e => {
                  handleChange({ ...filters, internal: e.target.checked })
                  trackEvent('users_scope_changed', {
                    internal: e.target.checked
                  })
                }}
              />
            )}
            {(user.admin || (user.roles.admin_roles && user.roles.admin_contact && !user.roles.readonly_contact)) && (
                <Button
                  onClick={() => {
                    trackEvent('user_create')
                    navigate(`${location.pathname}/check-email${location.search}`)
                  }}
                  disabled={Boolean(user.demo_mode)}
                  color={'primary'}
                  variant="contained"
                  startIcon={<i className="fa-solid fa-plus" />}
                  size="12px"
                >
                  {t('coupons.create.button')}
                </Button>
              )}
            </Box>
          </Box>
        </FormProvider>
      </ContentFilters>
    )
  }
  
  export default Filters
  
